/* Repairs for imported / hand-written layout JSON (DOM-free).
   The editor only understands openings as {wallId, t, width}, but people (and
   the LLMs they ask) tend to write them as segments {x1,y1,x2,y2} or points.
   Snap those onto the nearest wall, drop what can't be placed, and make sure
   every array exists. A well-formed doc comes back untouched. */
import type { Doc, Opening, OpeningType } from './types.ts';
import { distToSeg } from './geom.ts';
import { OPENING_DEFS } from './types.ts';

export interface NormalizeReport { placed: number; dropped: number }

/** A segment/point this close to a wall (inches) belongs to it. */
const SNAP_TOL = 12;

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function normalizeDoc(doc: Doc, rep: NormalizeReport = { placed: 0, dropped: 0 }): Doc {
  const d = doc as unknown as Record<string, unknown>;
  for (const k of ['walls', 'openings', 'fixtures', 'labels', 'constraints']) if (!Array.isArray(d[k])) d[k] = [];
  if (typeof doc.version !== 'number') doc.version = 1;
  const byId = new Map(doc.walls.map(w => [w.id, w]));

  doc.openings = doc.openings.filter(o => {
    if (!(o.type in OPENING_DEFS)) { rep.dropped++; return false; }
    if (byId.has(o.wallId) && num(o.t) && num(o.width)) return true;
    const s = o as unknown as Partial<Record<'x1' | 'y1' | 'x2' | 'y2' | 'x' | 'y', unknown>>;
    // segment → center + length; bare point → center only
    let cx: number, cy: number, len = num(o.width) ? o.width : 0;
    if (num(s.x1) && num(s.y1) && num(s.x2) && num(s.y2)) {
      cx = (s.x1 + s.x2) / 2; cy = (s.y1 + s.y2) / 2;
      len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1) || len;
    } else if (num(s.x) && num(s.y)) { cx = s.x; cy = s.y; }
    else { rep.dropped++; return false; }
    let best: { id: string; d: number; t: number; L: number } | null = null;
    for (const w of doc.walls) {
      const r = distToSeg(cx, cy, w.x1, w.y1, w.x2, w.y2);
      if (r.d <= SNAP_TOL && (!best || r.d < best.d)) best = { id: w.id, d: r.d, t: r.t, L: Math.hypot(w.x2 - w.x1, w.y2 - w.y1) };
    }
    if (!best) { rep.dropped++; return false; }
    if (!(len > 1)) len = OPENING_DEFS[o.type as OpeningType].width;
    len = Math.min(len, best.L);
    const fixed: Opening = {
      id: o.id, type: o.type, wallId: best.id, width: len,
      t: Math.round(Math.max(len / 2, Math.min(best.L - len / 2, best.t)) * 1e6) / 1e6,   // no float noise in saved files
      ...(num(o.hinge) ? { hinge: o.hinge } : {}), ...(num(o.swing) ? { swing: o.swing } : {}),
    };
    for (const k of Object.keys(o)) delete (o as unknown as Record<string, unknown>)[k];
    Object.assign(o, fixed);
    rep.placed++;
    return true;
  });
  return doc;
}
